import React from 'react';
import { useTranslation } from 'react-i18next';
import { Lock } from 'lucide-react';
import { LockedInsight } from 'modules/examInsights';
import { RESULT_ACCENTS, toneForPercent, type ResultCategoryKey } from './accents';
import type { CategoryResultData } from './categoryResult';
import { AnswerMap, MeterRow, ResultCard, type MapState } from './primitives';
import { mistakeList, questionTiming, type AnalysisAnswer } from './analysisRows';

/** Mistake rows shown before the rest are left to the answer review below. */
const MAX_MISTAKES = 12;

interface SkillAnalysisProps {
  category: ResultCategoryKey;
  data: CategoryResultData;
  answers: AnalysisAnswer[];
  /** The deeper analysis is behind the plan: parts and the answer map stay, the rest becomes a gate. */
  locked: boolean;
  /** Names the sections: "Parts" for Listening/Reading, "Modules" for SAT. */
  partsTitle: string;
  onSelectQuestion?: (n: number) => void;
  /** From the API: question-type scores, and time per section. Locked, only the matching `*Locked` flag arrives. */
  questionTypes?: Array<{ type: string; correct: number; total: number }>;
  questionTypesLocked?: boolean;
  sectionTiming?: Array<{ sectionId: string; sectionTitle: string; usedSeconds: number; limitSeconds: number | null }>;
  sectionTimingLocked?: boolean;
  /** The page already shows its own parts card or answer map (a DTM or Milliy result): leave that one out. */
  showParts?: boolean;
  showMap?: boolean;
  className?: string;
}

function LockPill({ label }: { label: string }) {
  return (
    <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-[#F1F4FB] px-2 py-0.5 align-middle text-[10.5px] font-bold text-[#4B5468] dark:bg-white/10 dark:text-muted-foreground">
      <Lock className="h-3 w-3" aria-hidden />
      {label}
    </span>
  );
}

/**
 * What sits under an IELTS/SAT/DTM hero: score per part (always shown), question types and every mistake with its
 * key and explanation (a gate while locked), and the answer map. Locked, nothing real is rendered behind the gate.
 */
export function SkillAnalysis({ category, data, answers, locked, partsTitle, onSelectQuestion, questionTypes, questionTypesLocked, sectionTiming, sectionTimingLocked, showParts = true, showMap = true, className }: SkillAnalysisProps) {
  const { t } = useTranslation();
  const accent = RESULT_ACCENTS[category];
  const stateLabels: Record<MapState, string> = {
    ok: t('result.view.correct'),
    no: t('result.view.wrong'),
    skip: t('result.view.skipped'),
  };
  const mistakes = mistakeList(answers);
  // Question types and time per section are IELTS/SAT analysis; the other categories read by topic.
  const standardized = category === 'ielts' || category === 'sat';
  const types = standardized && !questionTypesLocked ? (questionTypes ?? []) : [];
  const timing = standardized && !sectionTimingLocked ? (sectionTiming ?? []) : [];
  const perQuestion = standardized && !locked ? questionTiming(answers) : null;
  const typeLabel = (type: string) => t(`result.view.qtype.${type}`, { defaultValue: type });

  return (
    <div className={className}>
      {showParts && data.topics.length > 1 && (
        <ResultCard accent={accent} title={partsTitle} className="mt-3 lg:mt-4">
          <div className="mt-2">
            {data.topics.map((part) => (
              <MeterRow
                key={part.id}
                label={
                  <>
                    {part.title}
                    {locked && <LockPill label={t('result.view.analysis_chip')} />}
                  </>
                }
                value={`${part.correct} / ${part.total}`}
                percent={part.percent}
                color={toneForPercent(part.percent, 80, 60)}
              />
            ))}
          </div>
          <p className="mt-4 text-xs leading-normal text-[#4B5468] dark:text-muted-foreground">
            {locked ? t('result.view.parts_hint_locked') : t('result.view.parts_hint_open')}
          </p>
        </ResultCard>
      )}

      {standardized && (questionTypesLocked || types.length > 0) &&
        (questionTypesLocked ? (
          <LockedInsight
            className="mt-3 lg:mt-4"
            title={t('result.view.qtypes_title')}
            description={t('result.view.qtypes_locked_body')}
            lines={4}
          />
        ) : (
          <ResultCard accent={accent} title={t('result.view.qtypes_title')} className="mt-3 lg:mt-4">
            <div className="mt-2">
              {types.map((row) => (
                <MeterRow
                  key={row.type}
                  label={typeLabel(row.type)}
                  value={`${Math.round((row.correct / row.total) * 100)}%`}
                  percent={(row.correct / row.total) * 100}
                  color={toneForPercent((row.correct / row.total) * 100)}
                  note={`${row.correct} / ${row.total}`}
                />
              ))}
            </div>
          </ResultCard>
        ))}

      {standardized && (sectionTimingLocked || timing.length > 0) &&
        (sectionTimingLocked ? (
          <LockedInsight
            className="mt-3 lg:mt-4"
            title={t('result.view.timing_title')}
            description={t('result.view.timing_locked_body')}
            lines={3}
          />
        ) : (
          <ResultCard accent={accent} title={t('result.view.timing_title')} className="mt-3 lg:mt-4">
            <div className="mt-2">
              {timing.map((row) => (
                <MeterRow
                  key={row.sectionId}
                  label={row.sectionTitle || t('result.view.time')}
                  value={
                    row.limitSeconds
                      ? t('result.view.timing_of', { used: Math.round(row.usedSeconds / 60), limit: Math.round(row.limitSeconds / 60) })
                      : t('result.view.timing_min', { used: Math.round(row.usedSeconds / 60) })
                  }
                  percent={row.limitSeconds ? (row.usedSeconds / row.limitSeconds) * 100 : 0}
                  color={row.limitSeconds && row.usedSeconds >= row.limitSeconds ? '#B42318' : undefined}
                />
              ))}
            </div>
          </ResultCard>
        ))}

      {perQuestion && (
        <ResultCard accent={accent} title={t('result.view.pace_title')} className="mt-3 lg:mt-4">
          <div className="mt-2">
            {perQuestion.averages.map((row) => (
              <MeterRow key={row.title} label={row.title || t('result.view.time')} value={t('result.view.per_question', { seconds: row.seconds })} percent={Math.min(100, (row.seconds / 180) * 100)} />
            ))}
          </div>
          {perQuestion.long.count > 0 && (
            <p className="mt-4 text-[12.5px] leading-normal text-[#4B5468]">{t('result.view.long_questions', { count: perQuestion.long.count, wrong: perQuestion.long.wrong })}</p>
          )}
          {perQuestion.flagged.count > 0 && (
            <p className="mt-2 text-[12.5px] leading-normal text-[#4B5468]">
              {t('result.view.flagged_questions', { count: perQuestion.flagged.count, correct: perQuestion.flagged.correct, wrong: perQuestion.flagged.wrong })}
            </p>
          )}
        </ResultCard>
      )}

      {mistakes.length > 0 &&
        (locked ? (
          <LockedInsight
            className="mt-3 lg:mt-4"
            title={t('result.view.mistakes_title')}
            description={t('result.view.mistakes_locked_body')}
            lines={6}
          />
        ) : (
          <ResultCard accent={accent} title={t('result.view.mistakes_title')} aside={String(mistakes.length)} className="mt-3 lg:mt-4">
            <ul className="mt-2">
              {mistakes.slice(0, MAX_MISTAKES).map((row) => (
                <li key={row.n} className="border-t border-[#EEF1F8] py-3 first:border-t-0 dark:border-border">
                  <div className="flex items-start justify-between gap-3 text-[13.5px] font-bold text-foreground">
                    <span className="min-w-0">
                      {t('result.view.question_n', { n: row.n })}
                      {row.prompt && !row.partial && <span className="font-medium text-[#4B5468]">{`: ${row.prompt.length > 70 ? `${row.prompt.slice(0, 70)}…` : row.prompt}`}</span>}
                    </span>
                    {onSelectQuestion && (
                      <button
                        type="button"
                        onClick={() => onSelectQuestion(row.n)}
                        className="-my-3 inline-flex min-h-11 min-w-11 shrink-0 items-center justify-end rounded-md text-xs font-bold text-[color:var(--ra-fg)] underline-offset-2 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#10182B] lg:-my-1 lg:min-h-0 lg:min-w-0"
                        style={{ ['--ra-fg' as string]: accent.fg }}
                      >
                        {t('result.view.open_question')}
                      </button>
                    )}
                  </div>
                  <p className="mt-1.5 text-xs leading-normal text-[#4B5468] dark:text-muted-foreground">
                    {row.partial
                      ? t('result.view.passage_wrong', { count: row.itemsWrong })
                      : row.skipped
                        ? row.correctAnswer
                          ? `${t('result.view.skipped_answer')} → ${row.correctAnswer}`
                          : t('result.view.skipped_answer')
                        : row.correctAnswer
                          ? `${row.yourAnswer} → ${row.correctAnswer}`
                          : row.yourAnswer}
                  </p>
                  {row.explanation && <p className="mt-1.5 text-xs leading-normal text-[#364055] dark:text-foreground/80">{row.explanation}</p>}
                </li>
              ))}
            </ul>
            {mistakes.length > MAX_MISTAKES && (
              <p className="mt-3 text-xs text-[#646D84]">{t('result.view.more_mistakes', { count: mistakes.length - MAX_MISTAKES })}</p>
            )}
          </ResultCard>
        ))}

      {showMap && data.cells.length > 0 && (
        <ResultCard accent={accent} title={t('result.view.answer_map')} subtitle={t('result.view.answer_map_hint')} className="mt-3 lg:mt-4">
          <AnswerMap cells={data.cells} marks={data.cells.length <= 40} onSelect={onSelectQuestion} stateLabels={stateLabels} className="!grid-cols-5 sm:!grid-cols-8 md:!grid-cols-10 lg:!grid-cols-[repeat(20,minmax(0,1fr))]" />
          <div className="mt-4 flex flex-wrap gap-4 text-[12.5px] font-bold text-[#4B5468]">
            <span className="text-[#166534]">✓ {t('result.view.correct')}</span>
            <span className="text-[#B42318]">✗ {t('result.view.wrong')}</span>
            <span>– {t('result.view.skipped')}</span>
          </div>
        </ResultCard>
      )}
    </div>
  );
}
